import { sanitizeExtra } from '@code-expert/logging';
import type { Logger as ClientLogger, LogLevel, Serializable } from '@code-expert/logging';
import { makeBrowserLogger } from '@code-expert/logging/browser';
import { fromThrown, option, pipe, task } from '@code-expert/prelude';
import { invokeProcedure } from '/imports/ui/hooks/useProcedure';

const publish =
  (whenLoggedIn: boolean) =>
  <A>(
    level: LogLevel,
    message: string,
    extra?: Record<string, Serializable<A>>,
  ): task.Task<void> => {
    const safeExtra = pipe(sanitizeExtra(extra), option.toUndefined);
    return async () => {
      try {
        const { href, pathname, search } = window.location;
        await task.run(
          invokeProcedure('logger_store')({
            level,
            message,
            location: href ?? `${pathname ?? 'unknown'}${search ?? ''}`,
            ...(safeExtra != null ? { extra: safeExtra as $Inexpressible } : {}),
            whenLoggedIn,
          }),
        );
      } catch (e) {
        const error = fromThrown(e);
        Logger.error('Failed to publish to server logs', { error });
      }
    };
  };

export const Logger: ClientLogger & {
  persist: ClientLogger;
  persistAlways: ClientLogger;
} = {
  ...makeBrowserLogger(),
  /**
   * Logs and persists the log message to the server if the user is logged in.
   */
  persist: makeBrowserLogger(publish(true)),
  /**
   * Logs and persists the log message to the server, even for unauthenticated users.
   */
  persistAlways: makeBrowserLogger(publish(false)),
};
